import React, { useState, useMemo, useEffect } from 'react'
import { useField } from 'formik'
import {
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  ListSubheader,
  makeStyles,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core'
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft'
import ChevronRightIcon from '@material-ui/icons/ChevronRight'
import PropTypes from 'prop-types'
import { useTranslate } from 'react-admin'
import ExpandIcon from '../../../../assets/icons/expandIcon'
import mySelectFieldStyles from '../MySelectField/mySelectField.styles'
import courtType from '../../../../types/courtType'

const PAGE_SIZE = 10
const ADD_NEW_OPTION = 'addNewOptions'

const CourtSelectField = ({
  name,
  label,
  courts,
  emptyText,
  fullWidth,
  required,
  addOptionsComponent,
  ...otherProps
}) => {
  const [field, meta, helpers] = useField(name)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(0)
  const translate = useTranslate()
  const useStyles = makeStyles(mySelectFieldStyles)
  const classes = useStyles()

  const isError = meta.touched && (!!meta.error || (!field.value && required))

  const filtered = useMemo(
    () => (courts ?? []).filter((c) => c.name.toLowerCase().includes(search.toLowerCase())),
    [courts, search],
  )

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const visibleCourts = filtered.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)

  useEffect(() => setPage(0), [search])

  const handleChange = (e) => {
    if (e.target.value === ADD_NEW_OPTION) {
      return addOptionsComponent?.props.onClick()
    }
    helpers.setValue(e.target.value)
  }

  const handleClose = () => {
    setSearch('')
    setPage(0)
  }

  return (
    <FormControl
      {...otherProps}
      variant='filled'
      error={isError}
      required={required}
      fullWidth={fullWidth}
      classes={{ root: classes.formControlRoot }}
    >
      <InputLabel
        id={label}
        classes={{
          shrink: classes.shrink,
          formControl: classes.formControl,
          focused: classes.focused,
          error: classes.error,
        }}
      >
        {label}
      </InputLabel>
      <Select
        value={field.value ?? ''}
        onChange={handleChange}
        onBlur={field.onBlur}
        name={field.name}
        onClose={handleClose}
        labelId={label}
        disableUnderline
        IconComponent={ExpandIcon}
        style={{ borderRadius: '5px', height: '42px' }}
        classes={{ select: classes.select, filled: classes.selectInput }}
        MenuProps={{ PaperProps: { style: { maxHeight: 520 } }, autoFocus: false }}
        renderValue={(value) => (courts ?? []).find((c) => c.id === value)?.name ?? value}
      >
        <ListSubheader style={{ pointerEvents: 'auto', paddingTop: 8, paddingBottom: 4 }}>
          <TextField
            size='small'
            autoFocus
            fullWidth
            placeholder={translate('ra.action.search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
            onClick={(e) => e.stopPropagation()}
          />
        </ListSubheader>

        {visibleCourts.map((court) => (
          <MenuItem key={court.id} value={court.id}>
            {court.name}
          </MenuItem>
        ))}

        {filtered.length === 0 && (
          <MenuItem disabled>
            {emptyText ?? translate('ra.text.noOptionsAvailable')}
          </MenuItem>
        )}

        {pageCount > 1 && (
          <ListSubheader
            style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 4,
              pointerEvents: 'auto',
            }}
          >
            <IconButton
              size='small'
              disabled={page === 0}
              onClick={(e) => {
                e.stopPropagation()
                setPage((p) => p - 1)
              }}
            >
              <ChevronLeftIcon fontSize='small' />
            </IconButton>
            <Typography variant='caption'>
              {page + 1} / {pageCount}
            </Typography>
            <IconButton
              size='small'
              disabled={page >= pageCount - 1}
              onClick={(e) => {
                e.stopPropagation()
                setPage((p) => p + 1)
              }}
            >
              <ChevronRightIcon fontSize='small' />
            </IconButton>
          </ListSubheader>
        )}

        {courts?.length > 0 && addOptionsComponent && (
          <MenuItem value={ADD_NEW_OPTION}>{addOptionsComponent}</MenuItem>
        )}

        {!courts?.length && addOptionsComponent && (
          <div style={{ padding: '4px 16px' }}>{addOptionsComponent}</div>
        )}
      </Select>
      {meta.touched && meta.error && <FormHelperText>{meta.error}</FormHelperText>}
    </FormControl>
  )
}

CourtSelectField.propTypes = {
  name: PropTypes.string.isRequired,
  label: PropTypes.string,
  courts: PropTypes.arrayOf(courtType),
  emptyText: PropTypes.string,
  fullWidth: PropTypes.bool,
  required: PropTypes.bool,
  addOptionsComponent: PropTypes.node,
}

export default CourtSelectField
